import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Badge, Checkmark, Icon } from "@shared/ui";

import type {
  Carrier,
  PolicyLookupIntegration,
  SafePolicyLookupCredential,
} from "@platform/automatic-api";

import Add from "../../icons/Add";
import ChevronDown from "../../icons/ChevronDown";
import Info from "../../icons/Info";
import Pencil from "../../icons/Pencil";
import Search from "../../icons/Search";
import { getCarriers } from "../../lib/automatic";
import { getPolicyLookupCredentials } from "../../lib/policyLookupSettings";
import ButtonPrimary from "../ButtonPrimary";
import ButtonSecondary from "../ButtonSecondary";
import {
  CredentialForm,
  emptyCredentialForm,
  toCredentialFormState,
} from "./CredentialForm";
import type { IdentifierField } from "./CredentialForm";
import SettingsSection from "./SettingsSection";

/**
 * A single lienholder-portal integration's row: connection status, and its
 * expanded credential list/forms. One integration can cover several carrier
 * brands sharing the same portal login (e.g. sister brands under one
 * corporate umbrella, or every carrier routed through a third-party portal
 * like Lenderdock) — `carriers` lists all of them, and the row requires only
 * one shared credential rather than one per carrier. The row is titled after
 * the integration itself rather than its carriers, since some integrations
 * (Lenderdock) cover far too many carriers to list in the title.
 */
const IntegrationRow = ({
  clientId,
  integration,
  displayName,
  authPattern,
  carriers,
  credentials,
  onCredentialsChange,
}: {
  clientId: string;
  integration: string;
  displayName: string;
  authPattern: PolicyLookupIntegration["authPattern"];
  carriers: Carrier[];
  credentials: SafePolicyLookupCredential[];
  onCredentialsChange: (
    _integration: string,
    _credentials: SafePolicyLookupCredential[]
  ) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const connected = credentials.length > 0;
  // Maps the integration's configured authPattern to the field that acts as
  // the portal login identifier, so the right input(s) render below.
  const identifierField: IdentifierField =
    authPattern === "username-password"
      ? "username"
      : authPattern === "company-code"
      ? "companyCode"
      : authPattern === "business-name"
      ? "businessName"
      : "auto";
  // Without a configured authPattern we don't know which fields this
  // integration's portal actually needs, so credential collection is blocked
  // entirely rather than guessing.
  const isUnconfigured = authPattern === null;
  // Every integration in this list is a lienholder portal, so the subline is
  // always "Lienholder Portal" — trim it off the display name for the title
  // so it isn't shown twice (e.g. "Farmers Lienholder Portal" -> "Farmers").
  const integrationTitle = displayName
    .toLowerCase()
    .endsWith("lienholder portal")
    ? displayName
        .slice(0, displayName.length - "lienholder portal".length)
        .trim()
    : displayName;

  const upsertCredential = (credential: SafePolicyLookupCredential) => {
    const next = [...credentials];
    // `username` is always the match key, even for company-code/business-name
    // integrations — CredentialForm duplicates their identifier into
    // `username` since it's the database primary key.
    const index = next.findIndex(
      (existing) => existing.username === credential.username
    );
    if (index === -1) next.push(credential);
    else next[index] = credential;
    onCredentialsChange(integration, next);
  };

  const identityLabel = (credential: SafePolicyLookupCredential): string => {
    if (identifierField === "companyCode") {
      return credential.companyCode ?? credential.username;
    }
    if (identifierField === "businessName") {
      return credential.businessName ?? credential.username;
    }
    return credential.username;
  };

  return (
    <div
      className={`border rounded-lg bg-white transition ${
        expanded ? "border-gray-300 shadow-sm" : "border-gray-200"
      }`}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={displayName}
        className={`w-full flex items-center justify-between gap-x-4 px-5 py-4 text-left transition hover:bg-gray-50 ${
          expanded ? "bg-gray-50" : ""
        }`}
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="min-w-0">
          <span className="block text-sm font-bold text-gray-900">
            {integrationTitle}
          </span>
          <span className="mt-0.5 block text-xs text-gray-500">
            Lienholder Portal
          </span>
        </span>
        <span className="flex items-center gap-x-3 shrink-0">
          {connected ? (
            <span className="flex items-center gap-x-1 rounded bg-green-200 px-1.5 py-1 text-xs font-medium text-green-800">
              <Icon
                icon={Checkmark}
                height="h-3"
                width="w-3"
                className="fill-green-800"
              />
              Connected
            </span>
          ) : (
            <span className="text-xs text-gray-500">Not connected</span>
          )}
          <Icon
            icon={ChevronDown}
            height="h-3"
            width="w-3"
            className={`fill-gray-400 transition-transform duration-150 ease-in-out ${
              expanded ? "rotate-180" : "-rotate-90"
            }`}
            aria-hidden="true"
          />
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 px-5 py-2 flex flex-col divide-y divide-gray-100">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 py-3">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
              Supported carriers
            </span>
            {carriers.map((carrier) => (
              <span
                key={carrier.id}
                className="text-xs font-medium text-gray-700 bg-gray-100 rounded-full px-2.5 py-1"
              >
                {carrier.name}
              </span>
            ))}
          </div>
          {credentials.map((credential) =>
            editingKey === credential.username ? (
              <div key={credential.username} className="py-4">
                <CredentialForm
                  clientId={clientId}
                  integration={integration}
                  identifierField={identifierField}
                  initialForm={toCredentialFormState(credential)}
                  submitLabel="Save"
                  isUpdate
                  onCancel={() => setEditingKey(null)}
                  onSaved={(updated) => {
                    upsertCredential(updated);
                    setEditingKey(null);
                  }}
                />
              </div>
            ) : (
              <div
                key={credential.username}
                className="flex items-center justify-between gap-x-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {identityLabel(credential)}
                  </p>
                  <div className="mt-1">
                    <Badge status={credential.status} />
                  </div>
                </div>
                {!isUnconfigured && (
                  <ButtonSecondary
                    text="Edit"
                    type="button"
                    onClick={() => setEditingKey(credential.username)}
                    icon={Pencil}
                    className="shrink-0"
                  />
                )}
              </div>
            )
          )}

          <div className="py-4">
            {isUnconfigured ? (
              <div className="flex items-center gap-x-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
                <Icon
                  icon={Info}
                  height="h-4"
                  width="w-4"
                  className="fill-gray-400 shrink-0"
                />
                <span>
                  Contact Axle to set up accounts for this lienholder portal.
                </span>
              </div>
            ) : isAdding ? (
              <CredentialForm
                clientId={clientId}
                integration={integration}
                identifierField={identifierField}
                initialForm={emptyCredentialForm()}
                submitLabel="Connect"
                isUpdate={false}
                onCancel={() => setIsAdding(false)}
                onSaved={(created) => {
                  upsertCredential(created);
                  setIsAdding(false);
                }}
              />
            ) : connected ? (
              <ButtonSecondary
                text="Add another"
                type="button"
                onClick={() => setIsAdding(true)}
                icon={Add}
                className=""
              />
            ) : (
              <ButtonPrimary
                text="Connect"
                type="button"
                onClick={() => setIsAdding(true)}
                icon={Add}
                className=""
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PolicyLookupCredentialsSection = ({
  clientId,
  id,
}: {
  clientId: string;
  id?: string;
}) => {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [credentialsByIntegration, setCredentialsByIntegration] = useState<
    Map<string, SafePolicyLookupCredential[]>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // getPolicyLookupCredentials follows the [data, error] tuple
        // convention, so the inner pair is destructured directly.
        const [allCarriers, [credentialsData, credentialsError]] =
          await Promise.all([
            getCarriers(),
            getPolicyLookupCredentials(clientId),
          ]);

        if (credentialsError) {
          throw new Error(
            credentialsError.message ??
              "Could not load policy lookup credentials"
          );
        }

        // Carriers with no Policy Lookup integration at all shouldn't appear
        // in this list — there's nothing to connect a credential to.
        setCarriers(
          allCarriers.filter(
            (carrier) => carrier.policyLookupStatus !== "unsupported"
          )
        );

        // Accumulate credentials into a per-integration bucket so each
        // IntegrationRow only needs to look up its own slice by id.
        const grouped = new Map<string, SafePolicyLookupCredential[]>();
        (credentialsData?.credentials ?? []).forEach((credential) => {
          const existing = grouped.get(credential.integration) ?? [];
          grouped.set(credential.integration, [...existing, credential]);
        });
        setCredentialsByIntegration(grouped);
      } catch (err: any) {
        const message = err?.message || "Could not load policy lookup settings";
        setLoadError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [clientId, reloadKey]);

  const updateCredentialsForIntegration = (
    integration: string,
    credentials: SafePolicyLookupCredential[]
  ) => {
    setCredentialsByIntegration((current) => {
      const next = new Map(current);
      next.set(integration, credentials);
      return next;
    });
  };

  // Group carriers by integration rather than listing one row per carrier —
  // several carrier brands can share a single lienholder-portal login, and
  // that login is the actual unit of credential collection.
  const integrationGroups = useMemo(() => {
    const grouped = new Map<
      string,
      {
        authPattern: PolicyLookupIntegration["authPattern"];
        displayName: string;
        carriers: Carrier[];
      }
    >();
    carriers.forEach((carrier) => {
      carrier.policyLookupIntegrations
        // "public" integrations don't require a login at all, so there's no
        // credential for this section to collect for them.
        .filter(({ authPattern }) => authPattern !== "public")
        .forEach(({ id, displayName, authPattern }) => {
          // Accumulate carriers sharing an integration id under one group,
          // keeping the first-seen authPattern/displayName for that group.
          const existing = grouped.get(id);
          grouped.set(id, {
            authPattern: existing?.authPattern ?? authPattern,
            displayName: existing?.displayName ?? displayName,
            carriers: [...(existing?.carriers ?? []), carrier],
          });
        });
    });
    return Array.from(grouped.entries()).map(([integration, group]) => ({
      integration,
      ...group,
    }));
  }, [carriers]);

  const filteredIntegrationGroups = useMemo(
    () =>
      integrationGroups.filter((group) =>
        group.carriers.some((carrier) =>
          carrier.name.toLowerCase().includes(search.trim().toLowerCase())
        )
      ),
    [integrationGroups, search]
  );

  const connectedCount = useMemo(
    () =>
      integrationGroups.filter(
        (group) =>
          (credentialsByIntegration.get(group.integration) ?? []).length > 0
      ).length,
    [integrationGroups, credentialsByIntegration]
  );

  return (
    <SettingsSection
      id={id}
      title="Policy Lookup Credentials"
      subtitle="Connect a lienholder portal for each carrier so Axle can enable Policy Lookup."
    >
      {loading ? (
        <div className="flex flex-col gap-y-3">
          <div className="h-11 bg-gray-100 rounded animate-pulse" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-14 bg-gray-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-start gap-y-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
          <p>{loadError}</p>
          <ButtonSecondary
            text="Retry"
            type="button"
            onClick={() => setReloadKey((current) => current + 1)}
            icon={undefined}
            className=""
          />
        </div>
      ) : (
        <div className="flex flex-col gap-y-5">
          <div className="flex items-center justify-between gap-x-4">
            <div className="flex items-center bg-gray-50 rounded pl-3 grow">
              <Icon icon={Search} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Enter any carrier name..."
                className="bg-transparent text-sm grow p-3"
              />
            </div>
            <p
              className="text-xs text-gray-500 shrink-0 whitespace-nowrap"
              aria-label={`${connectedCount} of ${integrationGroups.length} connected`}
            >
              <span className="font-bold text-gray-900">{connectedCount}</span>{" "}
              of {integrationGroups.length} connected
            </p>
          </div>

          <div className="flex flex-col gap-y-3">
            {filteredIntegrationGroups.map((group) => (
              <IntegrationRow
                key={group.integration}
                clientId={clientId}
                integration={group.integration}
                displayName={group.displayName}
                authPattern={group.authPattern}
                carriers={group.carriers}
                credentials={
                  credentialsByIntegration.get(group.integration) ?? []
                }
                onCredentialsChange={updateCredentialsForIntegration}
              />
            ))}
            {filteredIntegrationGroups.length === 0 && (
              <div className="flex flex-col items-center gap-y-2 py-10 text-center">
                <Icon
                  icon={Search}
                  height="h-5"
                  width="w-5"
                  className="fill-gray-300"
                />
                <p className="text-sm text-gray-500">
                  {search.trim()
                    ? `No carriers match "${search.trim()}".`
                    : "No carriers available."}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </SettingsSection>
  );
};

export default PolicyLookupCredentialsSection;
